import cn from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';

import CroppedContent from 'shared/newComponents/CroppedContent';
import {
  ComponentProps,
  ComponentType
} from 'shared/entities/components/Component';
import { IListItemOptions } from 'shared/components/list';
import Typography from 'shared/newComponents/Typography';
import {
  TypographyColor,
  TypographySize,
  TypographyType
} from 'shared/newEntities/components/Typography';
import TransComp from 'shared/components/TransComp';
import WithLabel from 'shared/newComponents/WithLabel';
import { TranslationString } from 'shared/entities/localization';

import styles from './BaseItem.modules.scss';

export type BaseItemProps = IListItemOptions & {
  className?: string;
  limited?: { numLine: 1 | 2 };
  titleSize?: TypographySize;
  titleColor?: TypographyColor;
  label?: string | TranslationString;
  size?: 's' | 'm';
} & (
    | ComponentProps
    | {
        type: null;
      }
  );

const BaseItem = React.forwardRef<any, BaseItemProps>(
  (props: BaseItemProps, ref: React.Ref<any>) => {
    const {
      title,
      left = null,
      right,
      hoverable = true,
      isDisabled,
      isSelected,
      className,
      limited = { numLine: 1 },
      border,
      titleSize = TypographySize.m,
      titleColor = TypographyColor.main,
      titleIcon = null,
      label,
      size = 'm'
    }: BaseItemProps = props;

    const styleName = cn(
      'base-item',
      `base-item_${size}`,
      isSelected && 'base-item_selected',
      isDisabled && 'base-item_disabled',
      hoverable && 'base-item_hoverable',
      border && `base-item_${border}`
    );

    const titleElement = React.useMemo(() => {
      const limitedClass =
        limited &&
        cn(
          `base-item__title_limited base-item__title_limited_${titleSize}_${limited.numLine}`
        );

      const limitedBody = (
        <Typography
          fontType={TypographyType.text}
          fontSize={titleSize}
          fontColor={titleColor}
          styleName={cn('base-item__cropped-title', limitedClass)}
        >
          <TransComp>{title}</TransComp>
        </Typography>
      );

      return (
        <CroppedContent popupContent={title}>{limitedBody}</CroppedContent>
      );
    }, [title, titleSize, limited, titleColor]);

    const content = (
      <>
        {left && <div styleName="base-item__icon">{left}</div>}
        <Typography
          fontType={TypographyType.text}
          fontSize={titleSize}
          fontColor={titleColor}
          styleName="base-item__content"
        >
          {titleIcon && (
            <div styleName="base-item__content-icon">{titleIcon}</div>
          )}
          <div styleName="base-item__title">
            {label ? (
              <WithLabel
                label={label}
                labelClassName={styles['base-item__label']}
              >
                {titleElement}
              </WithLabel>
            ) : (
              titleElement
            )}
          </div>
          {right && <div styleName="base-item__right">{right}</div>}
        </Typography>
      </>
    );

    if (props.type === ComponentType.link) {
      return (
        <Link
          ref={ref}
          to={props.to}
          target={props.target}
          styleName={styleName}
          className={className}
        >
          {content}
        </Link>
      );
    }

    if (props.type === ComponentType.div) {
      return (
        <div
          ref={ref}
          data-testid="base-item"
          onClick={props.onClick}
          styleName={styleName}
          className={className}
        >
          {content}
        </div>
      );
    }

    return (
      <div ref={ref} styleName={styleName} className={className}>
        {content}
      </div>
    );
  }
);

export default BaseItem;
